import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
} from '@mui/material';
import { isEmpty } from 'lodash';
import { useSnackbar } from 'notistack';
import { COMPANY, COMPANY_USER_VALUE } from 'common/constants/user';
import { translateRoleIntoPermissions } from 'pages/UserPermissions/utils';
import { useAddUserToFirm } from 'services/hooks/useManageUser/useManageUser';
import { UserDetailsDialogProps, UserField } from './types';

const UserDetailsDialog = ({
  open,
  onClose,
  newPendingUsers,
  firmId,
  companyList,
  fundList,
  handleManageCompanyUsersDialogClose,
}: UserDetailsDialogProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState('');
  const [users, setUsers] = useState(newPendingUsers);
  const { enqueueSnackbar } = useSnackbar();
  const { mutate: addUserToFirm } = useAddUserToFirm();

  useEffect(() => {
    setCurrentIndex(0);
    setUsers(newPendingUsers);
  }, [open, newPendingUsers]);

  const handleSave = useCallback(async () => {
    try {
      const emailSet = new Set();
      users.forEach(user => {
        if (emailSet.has(user.email)) {
          throw new Error('Duplicate email addresses found');
        }
        emailSet.add(user.email);
      });

      const savePromises = users.map(async userData => {
        if (!userData.firstName || !userData.lastName) {
          throw new Error('First Name and Last Name are required');
        }

        const { companyId } = userData;
        // Avoid sending activation email to the user: `skip_email: true`
        const newUser = { ...userData, role_id: COMPANY_USER_VALUE, skip_email: true, firms_permissions: [] };
        const firmItem = { itemType: COMPANY, itemId: companyId };
        const permissions
          = translateRoleIntoPermissions({
            role: { id: COMPANY_USER_VALUE, firmItem },
            firmId,
            companyList,
            fundList,
          }) ?? [];
        addUserToFirm({ firmId, userData: newUser, permissions });
      });

      await Promise.all(savePromises);
      enqueueSnackbar('All user details saved successfully', { variant: 'success' });
      setError('');
      setUsers([]);
      onClose();
    } catch (saveError) {
      const errorMessage
        = saveError instanceof Error
          ? saveError.message
          : 'An error occurred while saving the user details. Please try again.';
      setError(errorMessage);
      enqueueSnackbar(
        saveError instanceof Error
          ? saveError.message
          : 'An error occurred while saving the user details. Please try again.',
        { variant: 'error' }
      );
    }
  }, [users, enqueueSnackbar, onClose, firmId, companyList, fundList, addUserToFirm]);

  const handleNext = useCallback(() => {
    if (currentIndex < users.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setError('');
    }
  }, [currentIndex, users.length]);

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setError('');
    }
  }, [currentIndex]);

  const handleChange = useCallback((index: number, field: UserField, value: string) => {
    setUsers(prevUsers => {
      const updatedUsers = prevUsers.map((user, idx) => (idx === index ? { ...user, [field]: value } : user));
      return updatedUsers;
    });
  }, []);

  const hasError = useMemo(() => users.some(user => !user.firstName || !user.lastName), [users]);

  useEffect(() => {
    if (isEmpty(users)) {
      handleManageCompanyUsersDialogClose();
    }
  }, [users, handleManageCompanyUsersDialogClose]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Enter User Details for {users[currentIndex]?.email}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Email"
          type="text"
          fullWidth
          value={users[currentIndex]?.email}
          onChange={e => handleChange(currentIndex, 'email', e.target.value)}
        />
        <TextField
          margin="dense"
          label="First Name"
          type="text"
          fullWidth
          value={users[currentIndex]?.firstName ?? ''}
          onChange={e => handleChange(currentIndex, 'firstName', e.target.value)}
          error={!!error && !users[currentIndex]?.firstName}
          helperText={!!error && !users[currentIndex]?.firstName ? error : ''}
        />
        <TextField
          margin="dense"
          label="Last Name"
          type="text"
          fullWidth
          value={users[currentIndex]?.lastName ?? ''}
          onChange={e => handleChange(currentIndex, 'lastName', e.target.value)}
          error={!!error && !users[currentIndex]?.lastName}
          helperText={!!error && !users[currentIndex]?.lastName ? error : ''}
        />
        <div style={{ overflowX: 'auto' }}>
          <Stepper activeStep={currentIndex} alternativeLabel>
            {users.map(user => (
              <Step key={user.email}>
                <StepLabel>{user.email}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handlePrevious} color="primary" disabled={currentIndex === 0}>
          Previous
        </Button>
        <Button onClick={handleNext} color="primary" disabled={currentIndex === users.length - 1}>
          Next
        </Button>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Tooltip title={hasError ? 'Please fill in all required fields' : ''}>
          <span>
            <Button onClick={handleSave} color="primary" disabled={hasError}>
              Add Users
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default UserDetailsDialog;
